import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, RefreshCw } from "lucide-react";
import { translations } from "@/lib/i18n";
import { SMARTLINK_PLATFORMS, SMARTLINK_SOCIALS } from "@/lib/smartlinkPlatforms";
import { detectOS, getPreferredPlatformKey, getDeepLink, detectBrowserLang, injectMarketingPixels } from "@/lib/smartlinkUtils";
import { SmartlinkCountdown } from "@/components/SmartlinkCountdown";
import { SmartlinkAudioPlayer } from "@/components/SmartlinkAudioPlayer";
import { SmartlinkLeadForm } from "@/components/SmartlinkLeadForm";

const API = process.env.REACT_APP_BACKEND_URL;

const formatDate = (iso, locale) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(locale, { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
};

const setMetaTag = (attr, key, content) => {
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

export default function SmartlinkPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const isManageMode = searchParams.get("manage") === "1";
  const [lang] = useState(() => detectBrowserLang());
  const sl = translations[lang].smartlink;
  const locale = lang === "es" ? "es-ES" : lang === "pt" ? "pt-BR" : "en-US";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [os] = useState(() => detectOS());
  const [refreshing, setRefreshing] = useState(false);
  const leadFormRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/smartlinks/${slug}`);
        if (!cancelled) setData(res.data);
      } catch {
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  useEffect(() => {
    if (!data) return;
    document.title = `${data.title} — ${data.artist}`;
    setMetaTag("property", "og:title", `${data.title} — ${data.artist}`);
    setMetaTag("property", "og:description", sl.chooseService);
    setMetaTag("property", "og:image", data.cover_url);
    setMetaTag("property", "og:url", window.location.href);
    setMetaTag("name", "description", sl.chooseService);
    injectMarketingPixels();
  }, [data, sl.chooseService]);

  const handlePlatformClick = (platform) => {
    const url = data?.platform_links?.[platform.key];
    if (!url) {
      toast(sl.comingSoon, { description: platform.name });
      leadFormRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      leadFormRef.current?.querySelector("input")?.focus();
      return;
    }
    const target = getDeepLink(platform.key, url);
    if (target.startsWith("spotify:")) {
      window.location.href = target;
    } else {
      window.open(target, "_blank", "noopener,noreferrer");
    }
  };

  const handleRefreshLinks = async () => {
    setRefreshing(true);
    try {
      const res = await axios.post(`${API}/api/smartlinks/${slug}/refresh-links`);
      setData((d) => ({ ...d, platform_links: res.data.platform_links }));
      if (res.data.updated) {
        const names = res.data.new_platforms
          .map((key) => SMARTLINK_PLATFORMS.find((p) => p.key === key)?.name || key)
          .join(", ");
        toast.success(sl.refreshNewLinksFound, { description: names });
      } else {
        toast(sl.refreshNoNewLinks);
      }
    } catch {
      toast.error(sl.refreshError);
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center" data-testid="smartlink-loading">
        <Loader2 className="w-8 h-8 text-brand-blue animate-spin" />
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-6" data-testid="smartlink-not-found">
        <div className="max-w-md w-full text-center">
          <h1 className="font-heading text-2xl font-bold mb-3">{sl.notFoundTitle}</h1>
          <p className="text-neutral-400 text-sm mb-8">{sl.notFoundSubtitle}</p>
          <Link
            to="/"
            data-testid="smartlink-back-home-button"
            className="inline-flex items-center gap-2 text-sm font-semibold text-white px-6 py-3 rounded-full border border-white/15 hover:bg-white/[0.06] transition-all duration-300"
          >
            <ArrowLeft className="w-4 h-4" /> {sl.backHome}
          </Link>
        </div>
      </div>
    );
  }

  const isReleased = data.release_date
    ? new Date(data.release_date).getTime() <= Date.now()
    : data.status === "released";
  const showCountdown = Boolean(data.release_date) && !isReleased;
  const activeSocials = SMARTLINK_SOCIALS.filter((s) => data.social_links?.[s.key]);
  const preferredKey = getPreferredPlatformKey(os);
  // Already-released tracks only show platforms with a real, working direct link.
  // Pre-release tracks show every platform as a pre-save/pre-add CTA (even before
  // a real link is found), since capturing the fan's email IS the pre-save action
  // for platforms that aren't live yet.
  const availablePlatforms = isReleased
    ? SMARTLINK_PLATFORMS.filter((p) => Boolean(data.platform_links?.[p.key]))
    : SMARTLINK_PLATFORMS;
  const orderedPlatforms = preferredKey
    ? [...availablePlatforms].sort((a, b) => (a.key === preferredKey ? -1 : b.key === preferredKey ? 1 : 0))
    : availablePlatforms;

  return (
    <div className={`min-h-screen bg-[#0A0A0A] text-white flex items-start justify-center py-10 px-4 relative overflow-hidden ${data.preview_url ? "pb-24" : ""}`} data-testid="smartlink-page">
      {/* Ambient blurred backdrop from cover art */}
      <div className="fixed inset-0 -z-10" aria-hidden="true">
        <img src={data.cover_url} alt="" className="w-full h-full object-cover scale-125 blur-3xl opacity-30" />
        <div className="absolute inset-0 bg-[#0A0A0A]/75" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="w-full max-w-md relative"
      >
        <div className="rounded-3xl overflow-hidden border border-white/15 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)]">
          <div className="aspect-square w-full bg-neutral-900 relative">
            <img
              src={data.cover_url}
              alt={data.title}
              data-testid="smartlink-cover-art"
              className="w-full h-full object-cover"
            />
            {isManageMode && (
              <button
                type="button"
                onClick={handleRefreshLinks}
                disabled={refreshing}
                data-testid="smartlink-refresh-links-button"
                title={sl.manageModeHint}
                className="absolute top-3 right-3 flex items-center gap-1.5 text-[11px] font-semibold text-white bg-black/60 backdrop-blur-md border border-white/20 rounded-full px-3 py-1.5 hover:bg-black/80 transition-colors duration-300 disabled:opacity-60"
              >
                {refreshing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                {refreshing ? sl.refreshingLinks : sl.refreshLinksButton}
              </button>
            )}
          </div>

          {/* Dark glass info strip */}
          <div className="bg-black/50 backdrop-blur-xl px-6 sm:px-8 pt-6 pb-8">
            <span
              data-testid="smartlink-status-badge"
              className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.15em] rounded-full px-3 py-1 mb-4 ${
                isReleased ? "bg-[#0B1F4B] text-white" : "bg-brand-red text-white"
              }`}
            >
              {isReleased ? sl.released : sl.preRelease}
            </span>

            <h1 data-testid="smartlink-title" className="font-heading text-2xl sm:text-3xl font-bold text-white leading-tight mb-1">
              {data.title}
            </h1>
            <p data-testid="smartlink-artist" className="text-neutral-400 text-base font-medium">
              {data.artist}
            </p>

            {showCountdown && (
              <div className="mt-3">
                <p data-testid="smartlink-release-date" className="text-neutral-400 text-xs mb-2">
                  {sl.availableOn} {formatDate(data.release_date, locale)}
                </p>
                <SmartlinkCountdown targetDate={data.release_date} />
              </div>
            )}

            {data.artist_note && (
              <p data-testid="smartlink-artist-note" className="text-neutral-300 text-sm italic leading-relaxed mt-4 border-l-2 border-neutral-500 pl-3">
                "{data.artist_note}"
              </p>
            )}
          </div>

          {/* Light frosted-glass platform panel */}
          <div className="bg-white/85 backdrop-blur-2xl px-6 sm:px-8 pt-10 pb-6">
            <p className="text-neutral-900 text-sm font-bold mb-6">{sl.chooseService}</p>

            {orderedPlatforms.length === 0 && (
              <button
                type="button"
                onClick={() => {
                  leadFormRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
                  leadFormRef.current?.querySelector("input")?.focus();
                }}
                data-testid="smartlink-no-platforms-yet"
                className="w-full text-left text-sm text-neutral-500 py-4 hover:text-neutral-700 transition-colors duration-300"
              >
                {sl.noPlatformsYet}
              </button>
            )}

            <div className="flex flex-col divide-y divide-black/[0.06]">
              {orderedPlatforms.map((platform, idx) => {
                const Icon = platform.icon;
                const buttonLabel = isReleased ? sl.listenNow : platform.verb === "add" ? sl.preAdd : sl.preSave;
                const isRecommended = platform.key === preferredKey;
                return (
                  <motion.div
                    key={platform.key}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.15 + idx * 0.04, ease: "easeOut" }}
                    data-testid={`smartlink-platform-row-${platform.key}`}
                    className="flex items-center justify-between gap-3 py-3 hover:bg-black/[0.03] transition-colors duration-300 rounded-lg px-1 -mx-1"
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div
                        className="w-12 h-12 rounded-xl border border-black/[0.06] bg-black/[0.03] flex items-center justify-center shrink-0"
                        style={{ "--brand-color": platform.color }}
                      >
                        <Icon className="w-6 h-6 text-[color:var(--brand-color)]" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <span className="text-sm font-semibold text-neutral-800 truncate block">{platform.name}</span>
                        {isRecommended && (
                          <span data-testid="smartlink-recommended-badge" className="text-[9px] font-bold text-brand-blue uppercase tracking-wide leading-tight block">
                            {sl.recommended}
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => handlePlatformClick(platform)}
                      data-testid={`smartlink-platform-button-${platform.key}`}
                      className="text-xs font-bold px-4 py-2 rounded-full border border-neutral-300 text-neutral-600 hover:border-neutral-400 hover:bg-neutral-100 hover:text-neutral-900 transition-all duration-300 whitespace-nowrap shrink-0"
                    >
                      {buttonLabel}
                    </button>
                  </motion.div>
                );
              })}
            </div>

            {activeSocials.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.15 + orderedPlatforms.length * 0.04 + 0.1, ease: "easeOut" }}
                className="mt-6 pt-5 border-t border-black/[0.08] flex items-center justify-between"
                data-testid="smartlink-follow-artist"
              >
                <span className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">
                  {sl.followArtist} {data.artist}
                </span>
                <div className="flex items-center gap-2">
                  {activeSocials.map((s) => {
                    const Icon = s.icon;
                    return (
                      <a
                        key={s.key}
                        href={data.social_links[s.key]}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`smartlink-social-${s.key}`}
                        className="w-9 h-9 rounded-full border border-neutral-300 flex items-center justify-center hover:bg-neutral-900 hover:border-neutral-900 transition-colors duration-300 group"
                      >
                        <Icon className="w-4 h-4 text-neutral-700 group-hover:text-white transition-colors duration-300" />
                      </a>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </div>
        </div>

        <motion.div
          ref={leadFormRef}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5, ease: "easeOut" }}
          className="mt-6 rounded-2xl border border-white/10 bg-white/[0.05] backdrop-blur-xl p-6"
          data-testid="smartlink-lead-card"
        >
          <p className="text-white text-sm font-bold mb-4">{sl.formTitle}</p>
          <SmartlinkLeadForm slug={slug} labels={sl} />
        </motion.div>

        <div className="text-center mt-6">
          <Link to="/" className="text-xs text-neutral-500 hover:text-neutral-300 transition-colors duration-300" data-testid="smartlink-powered-by">
            {sl.poweredBy} <span className="font-bold text-white">Doble R Records</span>
          </Link>
        </div>
      </motion.div>

      {data.preview_url && <SmartlinkAudioPlayer previewUrl={`${API}/api/smartlinks/${slug}/preview-audio`} title={data.title} artist={data.artist} />}
    </div>
  );
}
